import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { ExceptionManager } from 'Core/logManager';
import { catalogLoad } from 'Core/catalogs/redux/actions';
import { CLEAR_REDUX } from 'Redux/actionTypes';
import { resetALL } from '../../../../../.globals/redux/actions';

//STATE INITIAL
const INIT_STATE = {
  loadingSnapshot: false,
  snapshot: null,
  errorSnapshot: false,
  loadingGetMarkerDetails: false,
  getMarkerDetailsResponse: null,
  getMarkerDetailsId: 0,
  showTimeline: false,
  clipRequestDate: '',
  selectMarkerDetail: {
    vehicleListMarkerDetailId: null,
    markerDetailIndex: 0,
  },
  // Local trail row data stored at click time so InfoBubble can render
  // the Location tab immediately (address, speed, event, time, lat/lng)
  // without waiting for the getMarkerDetails API response.
  trailPreviewData: null,
};

const route = 'modules/.common/components/InfoBubble/redux/slice';

// Fetch unit inputs for a specific vehicle, enriched with catalog titles.
const fetchUnitInputs = async (unitId, { dispatch, getState }) => {
  try {
    if (!unitId) return [];

    dispatch(catalogLoad(['inputLabels']));

    const response = await clientQuery(
      `query SearchUnitInputs($unitId: Int!) {
        unitInputs: searchUnitInputs(unitId: $unitId) {
          unitId
          position
          labelId
          onLabel
          offLabel
        }
      }`,
      { unitId: parseInt(unitId, 10) },
      endpoints.GRAPHQL_GENERAL
    );

    const unitInputs = response?.unitInputs || [];
    const inputLabels = getState()?.catalogsRedux?.inputLabels;

    if (inputLabels?.length && unitInputs.length) {
      return unitInputs.map((input) => {
        const catalogLabel = inputLabels.find((l) => l.id === input.labelId);
        return { ...input, title: catalogLabel?.title || null };
      });
    }

    return unitInputs;
  } catch (exc) {
    ExceptionManager(exc, route, 'fetchUnitInputs');
    return [];
  }
};

// Async thunk para obtener el snapshot (ETA link / video)
export const getSnapshotRequest = createAsyncThunk(
  `${route}/getSnapshotRequest`,
  async (payload, { rejectWithValue }) => {
    try {
      const { deviceId, unitTime, isEtaLink, companyId } = payload;

      const headers = isEtaLink
        ? {
            'x-api-key': process.env.COGNITO_API_KEY_V2,
            'app-id': 'cloud-react-public',
            ...(companyId ? { 'account-id': String(companyId) } : {})
          }
        : undefined;

      const data = await clientQuery(
        `query getVideoLastSnapshot($deviceId: String!, $unitTime: AWSDateTime!) {
          Snapshot: getVideoLastSnapshot (deviceId: $deviceId, unitTime: $unitTime) {
            id
            deviceId
            eventCode
            eventName
            thumbnailUrl
            duration
            mediaType
            url
            unitTime
          }
        }`,
        {
          deviceId: deviceId,
          unitTime: unitTime
        },
        endpoints.GRAPHQL_GENERAL_V2,
        headers
      );

      if (data && data.Snapshot) {
        return {
          ...data.Snapshot,
          pictureID: data.Snapshot.id
        };
      } else {
        return {};
      }
    } catch (exc) {
      ExceptionManager(exc, route, 'getSnapshotRequest');
      return rejectWithValue({ error: true });
    }
  }
);

// Async thunk: fetch marker details (Vehicle / Trail / Landmark / Geofence / RoutePoint)
export const getMarkerDetails = createAsyncThunk(
  `${route}/getMarkerDetails`,
  async (data, { dispatch, getState, rejectWithValue }) => {
    try {
      const { type, id, unitId } = data;

      let detail;
      const body = { type, id };

      if (type === "Trail") {
        detail = `
        ... on MarkerDetailTrail {
          vehicleTrail {
            id
            deviceId
            latitude
            longitude
            heading
            eventCode
            eventName
            address
            formattedAddress
            driverId
            driverName
            ecuEngineOdometer
            ecuSpeed
            ecuEngineHours
            engineHours
            engineOdometer
            speed
            ignitionStatus
            satelliteCount
            signal
            speedLimit
            unitTime
            state
            unitType
            zip
            hDop
            vDop
            deviceData
          }
          unit {
            id
            deviceCategory
            useEcuOdometer
            useEcuEngineHours
            useEcuSpeed
            label
            jbus
            vbus            
            typeId
            offsetEcuEngineOdometer
            offsetEcuEngineHours
            offsetEngineOdometer
            offsetEngineHours
            notes
            lastReading {
              deviceLabel
              temperatures
              dtc
              unitType
              landmark_id
              LastMessageTime
              satelliteCount
              deviceData
              hDop
              sdCard
            }
            esn
            unitVersionProduct
            asset {
              vin
              make
              model
              year
            }
            dtcCount {
              history
              active
            }
            timeZoneId
            timeZone {
              id
              name
            }
            crews {
              id
              firstName
              lastName
            }
            tags {
              id
              label
              color
              path
            }
          }
          linkedDevice {
            id
            esn
            deviceModelId
            deviceModelName
            manufacturerId
            manufacturerName
            simProviderName
            hasVideo
            liveMode
            firmware
            firmwareId
            label
          }
        }
      `;
        body.unitId = `${unitId}`;
      } else if (type === "Vehicle") {
        detail = `
      ... on MarkerDetailVehicle {
        unit {
          id
          deviceCategory
          useEcuOdometer
          useEcuEngineHours
          useEcuSpeed
          label
          jbus
          vbus
          driverId
          driverName
          driverExternalId
          typeId
          offsetEcuEngineOdometer
          offsetEcuEngineHours
          offsetEngineOdometer
          offsetEngineHours
          notes
          linkedUnitId
          maintenanceWarningEventsCount
          isOffline
          esn
          unitVersionProduct
          asset {
            vin
            make 
            model
            year
            licensePlate
          }
          lastReading {
            deviceId
            unitId
            ecuEngineOdometer
            ecuSpeed
            ecuEngineHours
            speed
            engineHours
            engineOdometer
            unitType
            deviceLabel
            unitTime
            eventCode
            eventName
            latitude
            longitude
            distance
            temperatures
            dtc
            heading
            landmark_id
            sdCard
            address {
              address
              city
              formatted
              state
              zip
            }
            deviceData
            LastMessageTime
            satelliteCount
            hDop
          }
          dtcCount {
            history
            active
          }
          timeZoneId
          timeZone {
            id
            name
          }
          crews {
            id
            firstName
            lastName
          }
          tags {
            id
            label
            color
            path
          }
        }
        linkedDevice {
            id
            esn
            deviceModelId
            deviceModelName
            manufacturerId
            manufacturerName
            simProviderName
            hasVideo
            liveMode
            firmware
            firmwareId
            label
        }
      }
    `;
      } else if (type === "Landmark") {
        detail = `
      ... on MarkerDetailLandmark {
        landmark {
          id
          companyId
          name
          tags {
            id
            label
            color
            path
          }
          latitude
          longitude
          shape
          radius
          address
          city
          zip
          state
          country
          note
          coordinates {
            latitude
            longitude
          }
        }
      }
      `;
      } else if (type === "Geofence") {
        detail = `
      ... on MarkerDetailGeofence {
        geofence {
          id
          companyId
          name
          tags {
            id
            label
            color
            path
          }
          shape
          radius
          alertOnType
          center_latlong
          contacts {
              id
              title
              address
              type
          }
          coordinates {
              latitude
              longitude
          }
        }
      }
      `;
      } else if (type === "RoutePoint") {
        detail = `
      ... on MarkerDetailRoutePoint {
        routePoint {
          
          id
          name
          routeId
          address
          city
          state
          landmarkId
          latitude
          longitude
          radius
          sequence
          note       
          radius   
          status

        }
      }
      `;
      }

      const result = await clientQuery(
        `query ($type: MarkerType!, $id: String! ${type === "Trail" ? ', $unitId: String' : ''}) {
        Marker: getMarkerDetail(type: $type, id: $id ${type === "Trail" ? ', unitId: $unitId' : ''}) {
          id,
          type,
          markerDetail {
            ${detail}
          }
        }
      }`,
        body,
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (result && result.Marker) {
        let markerData = result.Marker;

        // For Trail type the API returns the device ID as `Marker.id`,
        // but the reducer and InfoBubble track by trail-point ID.
        if (type === "Trail") {
          markerData = { ...markerData, id };
        }

        if (type === "Vehicle" || type === "Trail") {
          const targetUnitId = markerData?.markerDetail?.unit?.id;
          if (targetUnitId) {
            const unitInputs = await fetchUnitInputs(targetUnitId, { dispatch, getState });
            markerData = { ...markerData, unitInputs };
          }
        }

        return markerData;
      }

      throw new Error('Not marker data');
    } catch (exc) {
      ExceptionManager(exc, route, 'getMarkerDetails');
      return rejectWithValue({ error: true });
    }
  }
);

// Async thunk: fetch public marker detail (ETA link share)
export const getPublicMarkerDetails = createAsyncThunk(
  `${route}/getPublicMarkerDetails`,
  async (id, { rejectWithValue }) => {
    try {
      const data = await clientQuery(
        `
        query getPublicMarkerDetail($id: Int!) {
          result: getPublicMarkerDetail(id: $id)
        }
      `,
        { id },
        endpoints.GRAPHQL_GENERAL_V2,
        {
          'x-api-key': process.env.COGNITO_API_KEY_V2
        }
      );

      if (data?.result) {
        return JSON.parse(data.result);
      }
      return rejectWithValue({ error: true });
    } catch (exc) {
      ExceptionManager(exc, route, 'getPublicMarkerDetails');
      return rejectWithValue({ error: true });
    }
  }
);

//Slice
const infoBubbleSlice = createSlice({
  name: 'infoBubble',
  initialState: INIT_STATE,
  reducers: {
    reset(state, action) {
      return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE };
    },
    // Manual setter retained for backward compatibility with consumers
    // that dispatched the legacy `getMarkerDetailsResponse` action.
    getMarkerDetailsResponse(state, action) {
      const payload = action.payload?.data ?? action.payload;
      if (payload?.id && String(payload.id) !== String(state.getMarkerDetailsId)) {
        return;
      }
      state.getMarkerDetailsResponse = payload;
      state.loadingGetMarkerDetails = false;
    },
    getShowTimeline(state, action) {
      const payload = action.payload?.data ?? action.payload;
      state.showTimeline = payload;
    },
    getClipRequestDate(state, action) {
      const payload = action.payload?.data ?? action.payload;
      state.clipRequestDate = payload;
    },
    getVehicleListMarkerDetailId(state) {
      state.selectMarkerDetail = {
        markerDetailIndex: (state.selectMarkerDetail?.markerDetailIndex || 0) + 1,
        vehicleListMarkerDetailId: state.getMarkerDetailsId,
      };
    },
    setTrailPreviewData(state, action) {
      state.trailPreviewData = action.payload ?? null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetALL, () => INIT_STATE);

    // getSnapshotRequest
    builder.addCase(getSnapshotRequest.pending, (state) => {
      state.loadingSnapshot = true;
      state.errorSnapshot = false;
      state.snapshot = null;
    });
    builder.addCase(getSnapshotRequest.fulfilled, (state, action) => {
      state.loadingSnapshot = false;
      state.errorSnapshot = false;
      state.snapshot = action.payload;
    });
    builder.addCase(getSnapshotRequest.rejected, (state, action) => {
      state.loadingSnapshot = false;
      state.errorSnapshot = true;
      state.snapshot = action.payload || { error: true };
    });

    // getMarkerDetails (Vehicle / Trail / Landmark / Geofence / RoutePoint)
    builder.addCase(getMarkerDetails.pending, (state, action) => {
      const newId = action.meta.arg?.id;
      state.loadingGetMarkerDetails = true;
      if (state.getMarkerDetailsId != newId) {
        state.getMarkerDetailsResponse = null;
      }
      state.getMarkerDetailsId = newId;
    });
    // Guard against stale responses when the user switches markers mid-flight
    // (replaces the saga `takeLatest` cancellation).
    builder.addCase(getMarkerDetails.fulfilled, (state, action) => {
      state.loadingGetMarkerDetails = false;
      if (action.payload?.id && String(action.payload.id) !== String(state.getMarkerDetailsId)) {
        return;
      }
      state.getMarkerDetailsResponse = action.payload;
    });
    builder.addCase(getMarkerDetails.rejected, (state) => {
      state.loadingGetMarkerDetails = false;
      state.getMarkerDetailsResponse = { error: true };
    });

    // getPublicMarkerDetails
    builder.addCase(getPublicMarkerDetails.pending, (state, action) => {
      const newId = action.meta.arg;
      state.loadingGetMarkerDetails = true;
      if (state.getMarkerDetailsId != newId) {
        state.getMarkerDetailsResponse = null;
      }
      state.getMarkerDetailsId = newId;
    });
    builder.addCase(getPublicMarkerDetails.fulfilled, (state, action) => {
      state.loadingGetMarkerDetails = false;
      if (action.payload?.id && String(action.payload.id) !== String(state.getMarkerDetailsId)) {
        return;
      }
      state.getMarkerDetailsResponse = action.payload;
    });
    builder.addCase(getPublicMarkerDetails.rejected, (state) => {
      state.loadingGetMarkerDetails = false;
      state.getMarkerDetailsResponse = { error: true };
    });

    // Legacy CLEAR_REDUX matcher: accept "" or "INFO_BUBBLE" as option.
    builder.addMatcher(
      (action) => action.type === CLEAR_REDUX && ["", "INFO_BUBBLE"].includes(action.payload?.option),
      (state, action) => {
        return action.payload?.parameters
          ? { ...state, ...action.payload.parameters }
          : { ...INIT_STATE };
      }
    );
  },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = infoBubbleSlice;

// Extract and export each action creator by name
export const {
  reset,
  getMarkerDetailsResponse,
  getShowTimeline,
  getClipRequestDate,
  getVehicleListMarkerDetailId,
  setTrailPreviewData,
} = actions;

// Export the reducer, either as a default or named export
export default reducer;
